import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  useAlerts,
  useBrandOverlay,
  useConnections,
  useFixtures,
  useModels,
  useProviders,
  useSchedulerStatus,
  useThreads,
} from "@/api/queries";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { AlertIcon, ArrowRightIcon, BellIcon, CheckIcon, DatabaseIcon } from "@/components/icons";
import { useBrand } from "@/theme/context";

/**
 * What needs somebody, in one strip — and nothing at all when nothing does.
 *
 * A breached alert, a failing schedule, a source whose health check wants
 * attention. Each is a standing state the reader can act on from the link;
 * an empty strip is not rendered, because "all clear" printed every morning
 * is a line people learn to skip, and then they skip the morning it isn't.
 */
export function AttentionStrip() {
  const { data: scheduler } = useSchedulerStatus();
  const { data: alerts } = useAlerts();
  const { data: connections } = useConnections();

  const breached = (alerts ?? []).filter((rule) => rule.enabled && rule.state === "breached");
  const failing = scheduler?.failing_schedules ?? 0;
  const unhealthy = (connections ?? []).filter((source) => source.health_verdict === "attention");

  const items: ReactNode[] = [];
  for (const rule of breached.slice(0, 3)) {
    const label = (
      <>
        <BellIcon className="size-4 shrink-0" />
        {rule.name || rule.metric_label} is breached
      </>
    );
    const className = "text-error hover:underline inline-flex items-center gap-2";
    items.push(
      rule.subject_kind === "dashboard" ? (
        <Link
          key={`alert-${rule.id}`}
          to="/dashboards/$dashboardId"
          params={{ dashboardId: rule.subject_id }}
          className={className}
        >
          {label}
        </Link>
      ) : (
        <Link
          key={`alert-${rule.id}`}
          to="/reports/$reportId"
          params={{ reportId: rule.subject_id }}
          className={className}
        >
          {label}
        </Link>
      ),
    );
  }
  if (breached.length > 3) {
    items.push(
      <span key="alerts-more" className="text-error">
        and {breached.length - 3} more alert{breached.length - 3 === 1 ? "" : "s"}
      </span>,
    );
  }
  if (failing) {
    // ALERT-1: the in-app half of operator notices — the one that still
    // works when SMTP is what broke.
    items.push(
      <Link
        key="schedules"
        to="/settings/schedules"
        className="text-error hover:underline inline-flex items-center gap-2"
      >
        <AlertIcon className="size-4 shrink-0" />
        {failing} schedule{failing === 1 ? " is" : "s are"} failing
      </Link>,
    );
  }
  for (const source of unhealthy.slice(0, 2)) {
    items.push(
      <Link
        key={`source-${source.id}`}
        to="/sources/$connectionId"
        params={{ connectionId: source.id }}
        search={{ tab: "health" as const }}
        className="text-warning hover:underline inline-flex items-center gap-2"
      >
        <DatabaseIcon className="size-4 shrink-0" />
        {source.name} needs attention
      </Link>,
    );
  }

  if (!items.length) return null;
  return (
    <section
      aria-label="Needs attention"
      className="bg-error-subtle/60 border-error/20 rounded-container type-support flex flex-wrap items-center gap-x-6 gap-y-2 border px-4 py-3"
    >
      {items}
    </section>
  );
}

/**
 * One line of the first-run checklist.
 *
 * Every row reads real deployment state. It used to carry an `arriving` chip
 * for steps a milestone had not built yet, which quietly outlived the
 * milestone: the model-provider row was still hardcoded to "not done · M2" two
 * milestones after providers shipped. A checklist that cannot complete is
 * worse than no checklist.
 */
function ChecklistRow({
  done,
  title,
  detail,
  action,
}: {
  done: boolean;
  title: string;
  detail: string;
  action?: ReactNode;
}) {
  return (
    <li className="flex items-start gap-3 py-3">
      <span
        className={
          done
            ? "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-success-subtle text-success"
            : "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-border-emphasized"
        }
      >
        {done ? <CheckIcon className="size-3" /> : null}
      </span>
      <div className="min-w-0 flex-1">
        <p className="type-label text-ink">{title}</p>
        <p className="type-support text-ink-secondary mt-0.5">{detail}</p>
        {action ? <div className="mt-1.5">{action}</div> : null}
      </div>
    </li>
  );
}

const GO = "type-support text-accent inline-flex items-center gap-1 hover:underline";

function Go({
  to,
  children,
}: {
  to: "/settings/theming" | "/sources" | "/settings/providers";
  children: ReactNode;
}) {
  return (
    <Link to={to} className={GO}>
      {children}
      <ArrowRightIcon className="size-3.5" />
    </Link>
  );
}

/**
 * The administrator's first run (ACC-2: setup is the Admin tier's).
 *
 * Shown only while something is left to do. Once every row is ticked it gets
 * out of the way — the front page belongs to the people asking questions, and
 * the same facts stay one click away on Sources, Models and Operations.
 */
export function SetupChecklist() {
  const { productName, theme } = useBrand();
  const { data: fixtureList } = useFixtures(true);
  const { data: connections } = useConnections();
  const { data: providers } = useProviders();
  const { data: models } = useModels();
  const { data: threads } = useThreads("");
  const { data: branding } = useBrandOverlay(true);

  const loaded = Boolean(fixtureList && connections && providers && models && threads && branding);
  const installed = (fixtureList?.fixtures ?? []).filter((fixture) => fixture.installed);
  const sources = connections ?? [];
  const documented = sources.filter((source) => source.has_data_dictionary).length;
  // "A provider exists" is not the check that matters — a key with no credit
  // passes it. What an administrator needs to know is whether a question can
  // be answered, which means an enabled provider offering at least one model.
  const enabled = (providers ?? []).filter((provider) => provider.enabled);
  const catalog = models ?? [];
  const unhealthy = enabled.filter((provider) => provider.last_status !== "ok");
  const answered = (threads ?? []).some((thread) => thread.message_count > 0);
  // "Branded" means this deployment changed something, not that a theme
  // loaded — a checklist row that is done before anybody does anything is the
  // row M4 had to fix.
  const branded = (branding?.overridden.length ?? 0) > 0;
  const checked = sources.filter((source) => source.health_verdict).length;

  const steps = [branded, installed.length > 0, sources.length > 0, catalog.length > 0, checked > 0, answered];
  const remaining = steps.filter((done) => !done).length;
  if (!loaded || remaining === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Finish setting up"
        description={`${remaining} step${remaining === 1 ? "" : "s"} left before this is ready to hand to analysts. Only administrators see this.`}
      />
      <CardBody>
        <ul className="divide-border grid gap-x-8 divide-y md:grid-cols-2 md:divide-y-0">
          <ChecklistRow
            done={branded}
            title="Brand the deployment"
            detail={
              branded
                ? `Rebranded here as ${productName} — the app, the unlock page, exports and scheduled emails all carry it.`
                : `Running on the ${theme?.brand.name ?? "default"} identity from tokens.json. Name, copy, colour, typefaces and logos are editable without touching a file.`
            }
            action={<Go to="/settings/theming">{branded ? "Adjust branding" : "Brand it"}</Go>}
          />
          <ChecklistRow
            done={installed.length > 0}
            title="Install demo data"
            detail={
              installed.length > 0
                ? `${installed.length} demo source${installed.length === 1 ? "" : "s"} ready to query.`
                : "Run the seed step to install the bundled databases."
            }
          />
          <ChecklistRow
            done={sources.length > 0}
            title="Connect a data source"
            detail={
              sources.length > 0
                ? `${sources.length} source${sources.length === 1 ? "" : "s"} configured, ${documented} with a data dictionary.`
                : "Add a database or upload a file, then browse its schema."
            }
            action={<Go to="/sources">{sources.length > 0 ? "Manage sources" : "Add a source"}</Go>}
          />
          <ChecklistRow
            done={catalog.length > 0}
            title="Add a model provider"
            detail={
              catalog.length > 0
                ? `${enabled.length} provider${enabled.length === 1 ? "" : "s"} offering ${catalog.length} model${catalog.length === 1 ? "" : "s"}${unhealthy.length ? ` · ${unhealthy.length} needs attention` : ""}.`
                : enabled.length > 0
                  ? "A provider is configured but offering no models — check its health, or pull a model onto the local runner."
                  : "Anthropic, OpenAI, AWS Bedrock or a local runner. A key in the environment becomes a provider on the next boot."
            }
            action={<Go to="/settings/providers">{catalog.length > 0 ? "Manage models" : "Add a provider"}</Go>}
          />
          <ChecklistRow
            done={checked > 0}
            title="Run a health check"
            detail={
              checked > 0
                ? `${checked} of ${sources.length} source${sources.length === 1 ? "" : "s"} verified — connectivity, schema, documentation, and four graded questions including a safety probe.`
                : "Before handing a source to analysts, prove the model can answer questions about it — and that an injected write in its notes is refused."
            }
            action={
              sources[0] ? (
                <Link
                  to="/sources/$connectionId"
                  params={{ connectionId: sources[0].id }}
                  search={{ tab: "health" as const }}
                  className={GO}
                >
                  {checked > 0 ? "Review health" : "Check a source"}
                  <ArrowRightIcon className="size-3.5" />
                </Link>
              ) : undefined
            }
          />
          <ChecklistRow
            done={answered}
            title="Ask the first question"
            detail={
              answered
                ? "This deployment has answered questions. Every one keeps the SQL that produced it."
                : "Use the box above: pick a source, ask in plain English, and watch the agent read the schema and run SQL."
            }
          />
        </ul>
      </CardBody>
    </Card>
  );
}
